import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2, X } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FirmsLiveCounter from "@/components/FirmsLiveCounter";
import FirmInboundCTA from "@/components/FirmInboundCTA";
import {
    getFirmTiers,
    registerFirm,
    loginFirm,
    setFirmSession,
    getFirmSession,
    getMeta,
} from "@/lib/api";

const TIER_ACCENT = {
    starter: "border-white/30",
    professional: "border-white",
    elite: "border-[#FF3B30]",
    enterprise: "border-[#FFCC00]",
};

export default function LawFirms() {
    const [tiers, setTiers] = useState([]);
    const [meta, setMeta] = useState(null);
    const [open, setOpen] = useState(null); // "register" | "login" | null
    const [busy, setBusy] = useState(false);
    const nav = useNavigate();

    const [reg, setReg] = useState({
        firm_name: "",
        contact_email: "",
        contact_name: "",
        jurisdictions: [],
        specialities: [],
        languages: [],
        states: "",
    });
    const [loginEmail, setLoginEmail] = useState("");

    useEffect(() => {
        getFirmTiers().then((d) => setTiers(d.tiers || [])).catch(() => {});
        getMeta().then(setMeta).catch(() => {});
        const s = getFirmSession();
        if (s?.firm_id) {
            // Already logged in
        }
    }, []);

    const toggle = (arr, v) =>
        arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

    const submitRegister = async () => {
        if (!reg.firm_name || !reg.contact_email) {
            toast.error("Firm name and email required");
            return;
        }
        setBusy(true);
        try {
            const firm = await registerFirm({
                ...reg,
                states: reg.states
                    ? reg.states.split(",").map((s) => s.trim()).filter(Boolean)
                    : [],
            });
            setFirmSession(firm);
            // Attribute outreach if the URL came from a pitch link (?ref=<code>)
            try {
                const params = new URLSearchParams(window.location.search);
                const ref = params.get("ref");
                if (ref && firm?.firm_id) {
                    const BACKEND = process.env.REACT_APP_BACKEND_URL;
                    await fetch(`${BACKEND}/api/outreach/attribute`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ code: ref, firm_id: firm.firm_id }),
                    });
                }
            } catch {
                /* attribution is best-effort */
            }
            toast.success(`Firm registered. Access code: ${firm.access_code}`);
            nav("/firm-portal");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Registration failed");
        } finally {
            setBusy(false);
        }
    };

    const submitLogin = async () => {
        if (!loginEmail) return;
        setBusy(true);
        try {
            const firm = await loginFirm(loginEmail);
            setFirmSession(firm);
            toast.success(`Welcome back, ${firm.firm_name}`);
            nav("/firm-portal");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    const allCaseTypes = meta?.case_types
        ? Object.entries(meta.case_types).flatMap(([_, items]) => items)
        : [];

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            {/* HERO */}
            <section className="border-b-2 border-white/20">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">
                        FOR LAW FIRMS / B2B
                    </div>
                    <h1
                        className="font-anton uppercase leading-[0.85] text-5xl sm:text-7xl md:text-8xl"
                        data-testid="firms-headline"
                    >
                        Qualified leads.<br />
                        <span className="text-[#FF3B30]">Pre-analysed</span>.<br />
                        Free for the people.
                    </h1>
                    <p className="mt-8 max-w-2xl font-sans text-white/70 text-base sm:text-lg leading-relaxed">
                        HPM users are free, forever. Firms pay to access analysed, jurisdiction-matched cases
                        with full 10-bot reports — strength score, violations, evidence gaps, recommended actions.
                        Stop hunting cold leads. Start claiming pre-qualified ones.
                    </p>
                    <div className="mt-8 max-w-3xl">
                        <FirmsLiveCounter />
                    </div>
                    <div className="mt-6 max-w-3xl">
                        <FirmInboundCTA sourceSlug="firms-landing-hero" />
                    </div>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <button
                            onClick={() => setOpen("register")}
                            className="btn-brutal btn-brutal-red"
                            data-testid="firm-register-cta"
                        >
                            REGISTER YOUR FIRM <ArrowRight size={16} />
                        </button>
                        <button
                            onClick={() => setOpen("login")}
                            className="btn-brutal"
                            data-testid="firm-login-cta"
                        >
                            FIRM LOGIN
                        </button>
                    </div>
                </div>
            </section>

            {/* TIERS */}
            <section className="border-b-2 border-white/20">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                        SUBSCRIPTION TIERS
                    </div>
                    <h2 className="font-anton text-4xl sm:text-6xl uppercase leading-none mb-12">
                        Pay only when you claim.
                    </h2>
                    <div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-2 border-white/20"
                        data-testid="tiers-grid"
                    >
                        {tiers.map((t, i) => (
                            <div
                                key={t.key}
                                data-testid={`tier-${t.key}`}
                                className={`p-6 lg:p-8 border-white/20 lg:[&:not(:nth-child(4n))]:border-r-2 [&:not(:last-child)]:border-b-2 lg:[&:nth-child(-n+4)]:border-b-0 ${TIER_ACCENT[t.key] || ""}`}
                            >
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">
                                    TIER 0{i + 1}
                                </div>
                                <div className="font-anton text-3xl uppercase mb-2">{t.label}</div>
                                <div className="font-anton text-5xl text-[#FF3B30] leading-none mb-1">
                                    ${t.monthly_fee.toFixed(0)}
                                    <span className="text-white/40 text-sm ml-1">/ MO</span>
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-white/50 mt-2 mb-4">
                                    {t.leads_limit_per_month >= 99999
                                        ? "UNLIMITED LEADS"
                                        : `${t.leads_limit_per_month} LEADS / MO`}
                                </div>
                                <p className="font-sans text-sm text-white/70 leading-relaxed mb-6">
                                    {t.blurb}
                                </p>
                                <ul className="space-y-2 mb-6">
                                    {[
                                        "Full 10-bot case reports",
                                        "Jurisdiction & specialty filters",
                                        "Lead claim audit log",
                                        "Referral attribution codes",
                                    ].map((f, j) => (
                                        <li
                                            key={j}
                                            className="font-sans text-xs text-white/80 flex gap-2"
                                        >
                                            <CheckCircle2
                                                size={12}
                                                className="text-[#FF3B30] flex-shrink-0 mt-0.5"
                                            />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={() => setOpen("register")}
                                    className="btn-brutal w-full justify-center text-[10px]"
                                    data-testid={`tier-cta-${t.key}`}
                                >
                                    GET STARTED
                                </button>
                            </div>
                        ))}
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-6">
                        START FREE ON STARTER · UPGRADE ANY TIME · CANCEL ANY TIME
                    </p>
                </div>
            </section>

            {/* HOW IT WORKS FOR FIRMS */}
            <section className="border-b-2 border-white/20">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20">
                    <h2 className="font-anton text-4xl sm:text-6xl uppercase leading-none mb-12">
                        How it works for firms.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 border-2 border-white/20">
                        {[
                            {
                                num: "01",
                                title: "Register & set practice",
                                copy: "Tell us your jurisdictions, specialties, and languages. Free to register on Starter.",
                            },
                            {
                                num: "02",
                                title: "Browse pre-analysed leads",
                                copy: "Every lead comes with the full 10-bot report, strength score, and recommended actions.",
                            },
                            {
                                num: "03",
                                title: "Claim & convert",
                                copy: "One click claims the case. Contact the user, convert, and grow.",
                            },
                        ].map((s) => (
                            <div
                                key={s.num}
                                className="p-8 border-white/20 md:[&:not(:last-child)]:border-r-2 [&:not(:last-child)]:border-b-2 md:[&:not(:last-child)]:border-b-0"
                            >
                                <div className="font-anton text-7xl text-white/15 leading-none">
                                    {s.num}
                                </div>
                                <div className="font-anton text-2xl uppercase mt-4">{s.title}</div>
                                <p className="font-sans text-sm text-white/70 mt-3 leading-relaxed">
                                    {s.copy}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer disclaimer={meta?.disclaimer || ""} />

            {/* MODALS */}
            {open && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                    data-testid="firm-modal"
                >
                    <div className="bg-[#0a0a0a] border-2 border-white max-w-xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="border-b-2 border-white/20 p-4 flex justify-between items-center sticky top-0 bg-[#0a0a0a]">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
                                {open === "register" ? "REGISTER FIRM" : "FIRM LOGIN"}
                            </div>
                            <button
                                onClick={() => setOpen(null)}
                                className="text-white/70 hover:text-[#FF3B30]"
                                data-testid="firm-modal-close"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-6">
                            {open === "login" ? (
                                <div className="space-y-4">
                                    <div className="font-anton text-3xl uppercase">Welcome back.</div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 block">
                                        CONTACT EMAIL
                                    </label>
                                    <input
                                        value={loginEmail}
                                        onChange={(e) => setLoginEmail(e.target.value)}
                                        placeholder="firm@example.com"
                                        className="w-full bg-[#111] border-2 border-white/20 px-4 py-3 font-sans text-white placeholder:text-white/30 focus:outline-none focus:border-white"
                                        data-testid="firm-login-email"
                                    />
                                    <button
                                        onClick={submitLogin}
                                        disabled={!loginEmail || busy}
                                        className="btn-brutal btn-brutal-red w-full justify-center"
                                        data-testid="firm-login-submit"
                                    >
                                        {busy ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                            <>LOG IN <ArrowRight size={14} /></>
                                        )}
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="font-anton text-3xl uppercase">Register your firm.</div>
                                    <p className="font-sans text-sm text-white/60">
                                        Start free on Starter tier. Upgrade only when you're ready to claim more leads.
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 block mb-1">
                                                FIRM NAME *
                                            </label>
                                            <input
                                                value={reg.firm_name}
                                                onChange={(e) =>
                                                    setReg((r) => ({ ...r, firm_name: e.target.value }))
                                                }
                                                placeholder="Williams & Park"
                                                className="w-full bg-[#111] border-2 border-white/20 px-3 py-2 text-sm focus:outline-none focus:border-white"
                                                data-testid="reg-firm-name"
                                            />
                                        </div>
                                        <div>
                                            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 block mb-1">
                                                CONTACT NAME
                                            </label>
                                            <input
                                                value={reg.contact_name}
                                                onChange={(e) =>
                                                    setReg((r) => ({ ...r, contact_name: e.target.value }))
                                                }
                                                placeholder="Full name"
                                                className="w-full bg-[#111] border-2 border-white/20 px-3 py-2 text-sm focus:outline-none focus:border-white"
                                                data-testid="reg-contact-name"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 block mb-1">
                                            CONTACT EMAIL *
                                        </label>
                                        <input
                                            type="email"
                                            value={reg.contact_email}
                                            onChange={(e) =>
                                                setReg((r) => ({ ...r, contact_email: e.target.value }))
                                            }
                                            placeholder="contact@firm.com"
                                            className="w-full bg-[#111] border-2 border-white/20 px-3 py-2 text-sm focus:outline-none focus:border-white"
                                            data-testid="reg-email"
                                        />
                                    </div>
                                    <div>
                                        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 block mb-1">
                                            JURISDICTIONS
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {(meta?.jurisdictions || []).map((j) => {
                                                const on = reg.jurisdictions.includes(j.value);
                                                return (
                                                    <button
                                                        key={j.value}
                                                        type="button"
                                                        onClick={() =>
                                                            setReg((r) => ({
                                                                ...r,
                                                                jurisdictions: toggle(
                                                                    r.jurisdictions,
                                                                    j.value
                                                                ),
                                                            }))
                                                        }
                                                        className={`text-xs font-mono uppercase tracking-wider px-2 py-1 border-2 ${on ? "bg-white text-black border-white" : "border-white/20 text-white/70"}`}
                                                    >
                                                        {j.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 block mb-1">
                                            SPECIALITIES (pick up to 8)
                                        </label>
                                        <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
                                            {allCaseTypes.map((c) => {
                                                const on = reg.specialities.includes(c.value);
                                                return (
                                                    <button
                                                        key={c.value}
                                                        type="button"
                                                        onClick={() =>
                                                            setReg((r) => ({
                                                                ...r,
                                                                specialities: toggle(
                                                                    r.specialities,
                                                                    c.value
                                                                ),
                                                            }))
                                                        }
                                                        className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${on ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-white/20 text-white/70"}`}
                                                    >
                                                        {c.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 block mb-1">
                                            STATES (comma-separated)
                                        </label>
                                        <input
                                            value={reg.states}
                                            onChange={(e) =>
                                                setReg((r) => ({ ...r, states: e.target.value }))
                                            }
                                            placeholder="California, Nevada, Arizona"
                                            className="w-full bg-[#111] border-2 border-white/20 px-3 py-2 text-sm focus:outline-none focus:border-white"
                                            data-testid="reg-states"
                                        />
                                    </div>
                                    <button
                                        onClick={submitRegister}
                                        disabled={busy}
                                        className="btn-brutal btn-brutal-red w-full justify-center"
                                        data-testid="reg-submit"
                                    >
                                        {busy ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                            <>REGISTER FIRM <ArrowRight size={14} /></>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
